/**
 * Pure list logic for the accessory catalog overlay: search, quick filters,
 * category tree, sorting and grouping. Kept free of Vue so it is unit-testable.
 * The one exception is `categoryLabel()` at the bottom, which needs the
 * dictionary to name the bucket this module invents.
 */
import { categoryPath, hasActiveAction, isEnriched, packageContent } from '@/helpers/ponCatalog'

/**
 * Virtual category bucket for accessories that are not in the Pon catalog.
 *
 * A sentinel, not a label: the value is the identity key of a tree node and of a
 * selected category path, so it must never collide with a real catalog category
 * and must not change when the wording does. `categoryLabel()` below maps it to
 * its dictionary label for display.
 */
export const OWN_DEALER_OPTIONS_CATEGORY = '__ownDealerOptions__'

export const isOwnDealerOptions = (name) => name === OWN_DEALER_OPTIONS_CATEGORY

const normalizeTerm = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, '')

/** Description/RemarksHtml can contain HTML; use this for plain-text contexts (list rows, search). */
export const plainText = (html) =>
  String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

export const displayTitle = (accessory) =>
  accessory?.PonAccessoryCatalog?.Title || accessory?.Name || ''

/**
 * What the accessories inside a package are called and numbered, so searching
 * for an accessory also turns up the packages that contain it. A member can be
 * a package itself — the detail panel drills into them — so the whole tree is
 * walked. Only the identifying fields count: a package is a hit because it
 * contains the thing searched for, not because a member's description mentions
 * it in passing.
 */
const packageMemberText = (accessory) =>
  packageContent(accessory).flatMap((member) => [
    member.AccessoryId,
    member.Catalog?.Title,
    ...packageMemberText({ PonAccessoryCatalog: member.Catalog }),
  ])

export const matchesSearch = (accessory, term) => {
  const needle = normalizeTerm(term)
  if (!needle) return true

  const pon = accessory?.PonAccessoryCatalog
  const haystacks = [
    accessory?.Name,
    accessory?.ID,
    accessory?.OptionType_ID,
    pon?.Title,
    plainText(pon?.Description),
    ...categoryPath(accessory),
    ...packageMemberText(accessory),
  ]
  return haystacks.some((value) => normalizeTerm(value).includes(needle))
}

export const QUICK_FILTERS = {
  all: () => true,
  action: (accessory, context) => hasActiveAction(accessory, context?.now),
  inStock: (accessory) => (accessory?.PonAccessoryCatalog?.Stock ?? 0) > 0,
  onQuote: (accessory, context) => (context?.selectedIds || []).includes(accessory?.OptionType_ID),
}

export const quickFilterCounts = (accessories, context) => {
  const counts = {}
  Object.keys(QUICK_FILTERS).forEach((key) => {
    counts[key] = accessories.filter((a) => QUICK_FILTERS[key](a, context)).length
  })
  return counts
}

/** The category path used for tree placement; non-enriched items get the virtual bucket. */
export const treePath = (accessory) => {
  const path = categoryPath(accessory)
  return path.length ? path : [OWN_DEALER_OPTIONS_CATEGORY]
}

/**
 * Builds a max 3 level category tree with counts per node.
 * Nodes: { name, path, count, children }. Sorted alphabetically (nl), with the
 * virtual own-dealer-options bucket last — it is placed by identity rather than
 * by its name, which is a sentinel and does not collate meaningfully.
 */
export const buildCategoryTree = (accessories) => {
  const root = []

  accessories.forEach((accessory) => {
    let level = root
    const path = []
    treePath(accessory).forEach((name) => {
      path.push(name)
      let node = level.find((n) => n.name === name)
      if (!node) {
        node = { name, path: [...path], count: 0, children: [] }
        level.push(node)
      }
      node.count++
      level = node.children
    })
  })

  const sortLevel = (nodes) => {
    nodes.sort((a, b) => {
      if (isOwnDealerOptions(a.name)) return 1
      if (isOwnDealerOptions(b.name)) return -1
      return a.name.localeCompare(b.name, 'nl')
    })
    nodes.forEach((node) => sortLevel(node.children))
  }
  sortLevel(root)

  return root
}

/** True when the accessory falls under the given tree path (partial paths match). */
export const matchesCategoryPath = (accessory, path) => {
  if (!path || !path.length) return true
  const own = treePath(accessory)
  return path.every((name, index) => own[index] === name)
}

const priceIncl = (accessory) => Number(accessory?.Price?.Incl) || 0

const byCategoryAndName = (a, b) => {
  const pathA = treePath(a)
  const pathB = treePath(b)
  // The virtual bucket for non-enriched items always sorts last
  if (pathA[0] !== pathB[0]) {
    if (isOwnDealerOptions(pathA[0])) return 1
    if (isOwnDealerOptions(pathB[0])) return -1
  }
  for (let i = 0; i < 3; i++) {
    const delta = (pathA[i] || '').localeCompare(pathB[i] || '', 'nl')
    if (delta) return delta
  }
  return displayTitle(a).localeCompare(displayTitle(b), 'nl')
}

export const SORTERS = {
  categoryName: byCategoryAndName,
  priceAsc: (a, b) => priceIncl(a) - priceIncl(b) || byCategoryAndName(a, b),
  priceDesc: (a, b) => priceIncl(b) - priceIncl(a) || byCategoryAndName(a, b),
}

/**
 * Groups a (sorted) list on the category level directly below the selected
 * tree path. Returns [{ name, items }]; a single unnamed group when grouping
 * adds nothing (deepest level reached).
 */
export const groupByLevelBelow = (accessories, selectedPath) => {
  const depth = selectedPath?.length || 0
  if (depth >= 3) return [{ name: null, items: accessories }]

  const groups = []
  accessories.forEach((accessory) => {
    const path = treePath(accessory)
    const name = path[depth] || path[path.length - 1]
    let group = groups.find((g) => g.name === name)
    if (!group) {
      group = { name, items: [] }
      groups.push(group)
    }
    group.items.push(accessory)
  })

  return groups
}

export default {
  OWN_DEALER_OPTIONS_CATEGORY,
  isOwnDealerOptions,
  plainText,
  displayTitle,
  matchesSearch,
  QUICK_FILTERS,
  quickFilterCounts,
  treePath,
  buildCategoryTree,
  matchesCategoryPath,
  SORTERS,
  groupByLevelBelow,
}

/**
 * Category names come from the Pon catalog and are shown as-is, except for the
 * bucket that collects accessories which are not in the catalog at all — that
 * one is a sentinel constant, not a name, and gets a dictionary label.
 *
 * Resolved per call, like `statusHelper.js`: `window.VUE_CLI_EXPORTS.plugins.i18n`
 * is only assigned while App.vue runs its setup, so a value captured at module
 * scope would be empty.
 */
export const categoryLabel = (name) =>
  isOwnDealerOptions(name)
    ? window.VUE_CLI_EXPORTS.plugins.i18n.t('/AccessoryCatalog/OwnDealerOptionsCategory')
    : name
